import React from 'react';
import classNames from 'classnames';

import type { PlanHighlight } from 'js/src/client';
import Button, { BUTTON_VARIANTS } from 'js/src/components/common/ui/Button';
import ContentPlaceholder from 'js/src/components/common/ui/ContentPlaceholder';

const PricingPlanHighlights = React.memo(function PricingPlanHighlights({
    highlights,
    showAndMore,
    andMoreClicked,
    className,
    topHighlight,
}: {
    highlights?: PlanHighlight[];
    showAndMore: boolean;
    andMoreClicked: (e: React.MouseEvent) => void;
    className?: string;
    // Renders an emphasized (bold + primary color) checkmark item at the top of the list,
    // right below the bold "Everything in ..., plus:" header.
    // - A string injects that text as a new emphasized item.
    // - A number promotes the existing highlight at that index to the top (and removes it
    //   from its normal position), keeping the copy matching the pricing page exactly.
    topHighlight?: string | number;
}) {
    let highlight_nodes;

    if (!highlights) {
        // Skeleton loading state
        highlight_nodes = [
            <div key="skeleton-0" className="my-l5-12">
                <ContentPlaceholder styleProps={{ width: '150px', height: '20px' }} />
            </div>,
            <li key="skeleton-1" className={classNames('my-l5-12', 'd-flex align-items-center')}>
                <i className="material-symbols-outlined icon-l5 text-success p-l5-4">check</i>
                <ContentPlaceholder styleProps={{ width: '120px', height: '16px' }} />
            </li>,
            <li key="skeleton-2" className={classNames('my-l5-12', 'd-flex align-items-center')}>
                <i className="material-symbols-outlined icon-l5 text-success p-l5-4">check</i>
                <ContentPlaceholder styleProps={{ width: '140px', height: '16px' }} />
            </li>,
            <li key="skeleton-3" className={classNames('my-l5-12', 'd-flex align-items-center')}>
                <i className="material-symbols-outlined icon-l5 text-success p-l5-4">check</i>
                <ContentPlaceholder styleProps={{ width: '100px', height: '16px' }} />
            </li>,
        ];
    } else {
        // A numeric topHighlight promotes the existing highlight at that index to the top;
        // skip it in its normal position so it isn't listed twice.
        const promotedIndex = typeof topHighlight === 'number' ? topHighlight : undefined;

        // Normal rendering with highlights data
        highlight_nodes = highlights
            .map((highlight, index) => {
                const className = 'my-l5-12';
                if (index === 0) {
                    return (
                        <div key={index} className={className}>
                            <b>{highlight.text1}</b>
                        </div>
                    );
                } else if (highlight.text2 === 'subheader') {
                    return (
                        <div key={index} className={className}>
                            <b>{highlight.text1}</b>
                        </div>
                    );
                } else if (index === promotedIndex) {
                    return null;
                } else {
                    return (
                        <li
                            key={index}
                            className={classNames(className, 'd-flex align-items-center')}
                        >
                            <i className="material-symbols-outlined icon-l5 text-success p-l5-4">
                                check
                            </i>
                            <span>{highlight.text1}</span>
                        </li>
                    );
                }
            })
            .filter((node): node is React.JSX.Element => node !== null);

        // The emphasized text to insert right after the bold "Everything in ..., plus:"
        // header (index 0): either the promoted highlight's text or a custom string.
        const emphasizedText =
            promotedIndex !== undefined ? highlights[promotedIndex]?.text1 : topHighlight;
        if (emphasizedText) {
            highlight_nodes.splice(
                1,
                0,
                <li
                    key="top-highlight"
                    className={classNames('my-l5-12', 'd-flex align-items-center')}
                >
                    <i className="material-symbols-outlined icon-l5 text-success p-l5-4">check</i>
                    <b className="color-primary">{emphasizedText}</b>
                </li>,
            );
        }
    }
    return (
        <div className="pricing-plan-highlights">
            <ul className={classNames('pricing-plan-highlights list-unstyled', className)}>
                {highlight_nodes}
            </ul>
            {showAndMore && highlights && (
                <div className="row">
                    <div className="col-md-12">
                        <Button
                            variant={BUTTON_VARIANTS.LINK}
                            text="and more..."
                            onClick={andMoreClicked}
                        />
                    </div>
                </div>
            )}
        </div>
    );
});

export default PricingPlanHighlights;
